import { useTranslation } from "@i18n";
import type { ComponentProps } from "react";
import React, { useEffect, useRef, useState } from "react";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { getAllUsersInSpace } from "@Actions/projectUsers";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import axiosWrapper from "@Services/axiosWrapper";
import type { SpacesDataResponse, SpaceDataItem } from "@ServerTypes/spaces/space.schema";
import { getBackendUrl } from "@Utils";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING } from "@Services/constants";
import { getInspectionUrl, formCategories, defaultModuleNamesMapper } from "@Helpers";
import { useNavigate, useParams } from "react-router-dom";
import FormPreview from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/FormTemplate/formPreview";
import { formCategoryObject } from "@Features/portal-settings";
import { Box } from "@mui/material";
import { TaskInspectionAndProgressForm as TasksForms } from "@Features/forms";
import useCreateInspection from "./useCreateInspection";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import { MileStones } from "@Features/contracts";
import ContractTasks from "./contractTaskGrid";
import { getContractDetails } from "@Features/contracts/state/contracts.actions";
import DatePickerComponent from "@CubeComponents/Mui_Form/DatePickerComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import TreeSelectDropDown from "@CubeComponents/html/TreeDropDown";
import FormFlowCreationNew from "../FormFlow/formFlowCreationNew";
import Text from "@CubeComponents/html/Text/Text";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FormFlowOption, TemplateTreeRow } from "./useCreateInspection";
import _ from "lodash";

/**
 * One node of the template tree as `TreeSelectDropDown` hands it back — the template row plus the two
 * display keys the mapper below adds for it.
 */
type TemplateTreeNode = TemplateTreeRow & { id?: ObjectIdString; label?: string };

/**
 * The slice of the forms grid's imperative handle the attach step reads.
 *
 * `TaskInspectionAndProgressForm` declares its handle as `{ slickGrid, refresh }` and does not export
 * it — `dataView` is not on it at all, even though this attach step has always read it. Declared here
 * and reported as a gap in that component's handle type.
 */
interface AttachGridHandle {
  dataView?: { getAllSelectedItems: () => Array<{ _id?: ObjectIdString }> };
}

interface InspectionCreationProps {
  projectId?: string;
  /** Whether the wizard modal is open, and which form category it is creating. */
  showFormFlowCreationModal: { status: boolean; category: string };
  setShowFormFlowCreationModal: (state: { status: boolean; category: string }) => void;
  /** `"contract"` or `"project"` — which screen opened the wizard. */
  source?: string;
  spaceId?: string | null;
  /**
   * Passed by the contract screen and forwarded into `useCreateInspection` by the spread below, which
   * does not read it. Declared so that spread stays honest.
   */
  setChangeOrderState?: unknown;
}

function InspectionCreation(props: InspectionCreationProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams() as { projectId: string; contractId?: string };
  const milestoneRef = useRef<AttachGridHandle | null>(null);
  const treeGrid = useRef<React.ComponentRef<typeof ContractTasks>>(null);
  const formsRef = useRef<AttachGridHandle | null>(null);
  const dispatch = useDispatch();
  const getProjectSettingObject = useSelector((state: RootState) => state.getProjectSettingObject);
  const portalData = useSelector((state: RootState) => state.portalData);
  const customModules = useSelector((state: RootState) => state.customModules);
  const contractDetails = useSelector((state: RootState) => state.contractDetails);

  const {
    title,
    description,
    dueDate,
    setDueDate,
    selectedForm,
    setSelectedForm,
    spaceId,
    setSpaceId,
    selectedFormFlow,
    setSelectedFormFlow,
    handleCancelFormAttachment,
    handleFormFlowCreate,
    attachEntity,
    setAttachEntity,
    handleCreateClick,
    progressUpdateEntityCondition,
    inspectionEntityCondition,
    isRustomjeePortal,
    isMPMRCLPortal,
    allowedAttachmentConstants,
    setRememberUserSettings,
    rememberUserSettings,
    templateDataSource,
    showPreview,
    setShowPreview,
    showCreateFlowModal,
    setShowCreateFlowModal,
    formFlowOptions,
    handleSelectTemplate,
    handleSetDescription,
    handleSetTitle,
    showLoader,
    formFlowFilter,
    handleFlowFilterChange,
    handleClearFlowFilter,
  } = useCreateInspection({
    mobile: false,
    projectId: props.projectId,
    ...props,
    formCategoryObject,
    getInspectionUrl,
    navigate,
    contractId: params.contractId,
    resourceType: props.showFormFlowCreationModal.category,
    portalData,
    getContractDetails,
  });

  const [_spacesOptions, setSpacesOptions] = useState<SpaceDataItem[]>([]);

  useEffect(() => {
    const fetchSpaces = async () => {
      if (props.projectId) {
        try {
          const response = await axiosWrapper.post<SpacesDataResponse>(`${getBackendUrl()}/spaces/spacesData`, {
            value: { projectId: props.projectId },
          });
          if (response.data?.result) {
            // SERVER SCHEMA GAP: `spacesDataResponse` types `result` as the DELETE-action shape
            // (`{ deletedSpaceName?, spaceName? }`), but the list call made here returns
            // `spaceDataItemSchema[]`. The union that covers both (`spacesDataResult`) exists in
            // `space.schema.ts` but the response schema does not use it. Asserted (erased at emit)
            // until the response models the list shape.
            setSpacesOptions(response.data.result as unknown as SpaceDataItem[]);
          }
        } catch (error) {
          console.error("Error fetching spaces:", error);
          Promise.reject(error);
        }
      }
    };
    fetchSpaces();
  }, [props.projectId]);

  useEffect(() => {
    if (spaceId) {
      dispatch(
        getAllUsersInSpace({
          spaceId,
        }),
      );
    }
  }, [spaceId]);

  // The Syncfusion-shaped change event both text fields emit; only `target.name` / `target.value` are
  // read, and both through `?.`.
  const handleChange = (args?: { target?: { name?: string; value?: string } }) => {
    // KNOWN BUG, preserved verbatim: the `?.` chain yields `undefined` for a field with no value, but
    // both callees call `.substring(0, 500)` on it unconditionally — so an empty change event throws.
    // Asserted (erased at emit); the fix is a guard in `useCreateInspection`.
    if (args?.target?.name == "title") {
      handleSetTitle(args?.target?.value!);
    } else if (args?.target?.name == "description") {
      handleSetDescription(args?.target?.value!);
    }
  };

  const formsHeaderContent = () => {
    const categoryObj = customModules.find((module) => module.resourceType == props.showFormFlowCreationModal.category);
    const formHeader =
      categoryObj?.formHeader ?? formCategoryObject[props.showFormFlowCreationModal.category]?.formHeader ?? "";
    return (
      <div className="flex w-full gap-2 items-center justify-between">
        <span>{`Create ${formHeader}`}</span>
        {props.source == "project" && (
          <SelectSpaces
            moduleName={props.showFormFlowCreationModal.category}
            data-testid="spaceDropdownField"
            allowBorder={true}
            projectId={props.projectId}
            selectedSpaceCallback={(params: { _id?: string }) => {
              setSpaceId(params._id);
            }}
          />
        )}
      </div>
    );
  };

  // The parameter is the full shape `SelectDropDown.onChange` declares, including the multi-select
  // array case this single-select dropdown never produces — hence the two assertions, both erased at
  // emit (the second parenthesises only the RECEIVER, so the optional chain still short-circuits).
  //
  // KNOWN BUG, preserved verbatim: `SelectDropDown` calls `onChange` with `null` when the selection is
  // cleared, and that `null` is written straight into state — after which every `selectedFormFlow.x`
  // read in this file throws. The fix is to fall back to `{}` here, which is a behaviour change.
  const handleFormFlowSelection = (args: FormFlowOption | FormFlowOption[] | null) => {
    setSelectedFormFlow(args as FormFlowOption);
    if ((args as FormFlowOption)?.formFlow?.length == 0) {
      dispatch(
        showToastNotification({
          content: t("This formFlow's flow is not defined"),
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  };

  const headerTemplateForFormFlow = () => {
    const filterButtonSx = (isActive: boolean) => ({
      borderRadius: "0.5rem",
      padding: "0.375rem 1rem",
      boxShadow: "none",
      color: "var(--primary-text)",
      ...(isActive
        ? {
            background: "var(--light-gray)",
            border: "1px solid transparent",
            "&:hover": { background: "#D8DEEC" },
          }
        : {
            background: "var(--surface-white)",
            border: "1px solid var(--lighter-gray)",
            "&:hover": { background: "var(--surface-hover-light)" },
          }),
    });

    return (
      <div className="flex border-b border-b-button_light !gap-3 p-3 -mt-3 -mb-3 w-full items-center">
        <Text className="font-semibold">{t("I'm part of")}</Text>
        <ButtonComponent
          onClick={() => handleFlowFilterChange("anyLevel")}
          sx={filterButtonSx(formFlowFilter === "anyLevel")}
        >
          {t("Any Level")}
        </ButtonComponent>
        <ButtonComponent
          onClick={() => handleFlowFilterChange("firstLevel")}
          sx={filterButtonSx(formFlowFilter === "firstLevel")}
        >
          {t("First Level")}
        </ButtonComponent>
      </div>
    );
  };

  const emptyListTemplate = () => {
    if (formFlowFilter) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 px-4 py-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-light_gray">
            <IconComponent name="Filter" width="1.75rem" height="1.75rem" color="var(--primary-text)" />
          </span>
          <Text className="text-text_light text-center">{t("No form flows match this filter")}</Text>
          <ButtonComponent
            buttonType={BUTTON_TYPES.TEXT_BUTTON}
            onClick={handleClearFlowFilter}
            sx={{ fontWeight: 600, color: "var(--primary-text)" }}
          >
            {t("Clear filter")}
          </ButtonComponent>
        </div>
      );
    } else {
      return (
        <div className="flex flex-col items-center justify-center gap-4 px-4 py-8">{t("No records to display")}</div>
      );
    }
  };

  const formsModelContent = () => {
    const categoryObj = customModules.find((module) => module.resourceType == props.showFormFlowCreationModal.category);

    const formHeader =
      categoryObj?.formHeader ?? formCategoryObject[props.showFormFlowCreationModal.category]?.formHeader ?? "";
    return (
      <div className="flex flex-col gap-6">
        <div className="flex gap-6 items-center">
          <span className="font-semibold w-3/12 gap-1 items-center flex">
            <Text className="font-semibold">{t("Template")}</Text>
            <span className="text-2xl font-light text-red-500">*</span>
          </span>
          <div className="w-9/12 flex gap-6">
            <div className="w-3/5">
              <TreeSelectDropDown
                placeholder={t("Select Template")}
                treeData={(templateDataSource || []).map((item) => ({
                  ...item,
                  id: item._id,
                  label: item.name,
                  parentId: item.parentId ?? null,
                }))}
                value={selectedForm?.name ?? ""}
                onSelectionChange={handleTemplateSelect}
                disabledParentSelection
              />
            </div>
            <div className="h-8 w-8 p-1 items-center rounded-sm bg-button_light">
              <IconComponent
                isEnabled={!!selectedForm._id}
                className="cursor-pointer"
                onClick={() => setShowPreview("Template")}
                name="preview"
                width="1.5rem"
                height="1.5rem"
              />
            </div>
          </div>
        </div>
        <div className="flex gap-6 items-center">
          <span className="font-semibold w-3/12 gap-1 items-center flex">
            <Text className="font-semibold">{t("Form Flow")}</Text>
            <span className="text-2xl font-light text-red-500">*</span>
          </span>
          <div className="w-9/12 flex items-center gap-6">
            <div className="w-3/5">
              <SelectDropDown
                headerTemplate={headerTemplateForFormFlow}
                emptyListTemplate={emptyListTemplate}
                placeholder={t("Select form flow")}
                onChange={handleFormFlowSelection}
                // Same under-declared `SelectDropDown.value` as `commentAndSignModal`: the prop is typed
                // as an option object, but the component's own display effect has a scalar branch
                // (`option[optionId] === (value?.[optionId] || value)`) that this relies on. Erased at emit.
                value={selectedFormFlow._id as unknown as FormFlowOption}
                disabled={!selectedForm._id}
                buttonType={BUTTON_TYPES.BORDER_BUTTON}
                fields={{
                  text: "name",
                  value: "_id",
                }}
                options={formFlowOptions.filteredOptions}
                buttonWidth="100%"
                popperWidth={400}
              />
            </div>
            <div className="h-8 w-8 p-1 items-center rounded-sm bg-button_light">
              <IconComponent
                isEnabled={!!selectedFormFlow._id}
                className="cursor-pointer"
                onClick={() => setShowPreview("FormFlow")}
                name="preview"
                width="1.5rem"
                height="1.5rem"
              />
            </div>
            {getProjectSettingObject.formFlow?.add?.enable && (
              <>
                <div>{"or"}</div>
                <div>
                  <ButtonComponent
                    disabled={!selectedForm._id || !spaceId}
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleFormFlowCreate}
                  >
                    <div className="flex items-center gap-1">
                      <IconComponent isEnabled={!!selectedForm._id} name="add" width="1rem" height="1rem" />
                      <Text>{t("New Flow")}</Text>
                    </div>
                  </ButtonComponent>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-6 items-center">
          <span className="font-semibold w-3/12 gap-1 items-center flex">
            <Text>{t("Title")}</Text>
            <span className="text-2xl font-light text-red-500">*</span>
          </span>
          <span className="w-9/12">
            <TextFieldComponent
              name="title"
              placeholder={t("Title")}
              showClearButton
              value={title}
              onChange={handleChange}
            />
          </span>
        </div>
        <div className="flex gap-6 items-center">
          <Text className="font-semibold w-3/12">{t("Description")}</Text>
          <span className="w-9/12">
            <TextFieldComponent
              name="description"
              placeholder={t("Description")}
              showClearButton
              value={description}
              multiline={true}
              rows={3}
              onChange={handleChange}
            />
          </span>
        </div>
        {!isMPMRCLPortal && (
          <div className="flex gap-6 items-center">
            <div className="font-semibold w-3/12 gap-1 items-center flex">{t("Due Date")}</div>
            <div className="w-9/12">
              <div className="w-3/5">
                <DatePickerComponent
                  min={new Date()}
                  value={dueDate ? new Date(dueDate) : undefined}
                  id="datetimepicker"
                  width="100%"
                  onChange={(args: { value?: string | number | Date | null }) => {
                    if (args.value && new Date(args.value).getTime() > Date.now()) {
                      setDueDate(new Date(args.value).setHours(23, 59, 0, 0));
                    } else {
                      setDueDate(null);
                    }
                  }}
                  placeholder={t("Enter Due Date")}
                  format="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>
        )}
        {props.source == "contract" && (progressUpdateEntityCondition || inspectionEntityCondition) && (
          <div className="flex gap-6 items-center">
            <span className="font-semibold w-3/12">{`${formHeader} based on`}</span>
            <span className="flex w-9/12 justify-between">
              <span className="w-full flex gap-5">
                {progressUpdateEntityCondition && (
                  <RadioButton
                    label="Progress Updates"
                    onChange={() =>
                      setAttachEntity({
                        name: formCategories.progressUpdates,
                      })
                    }
                    checked={
                      attachEntity.name === formCategories.progressUpdates ||
                      // `noUncheckedIndexedAccess` adds `| undefined` to every index-signature read, and
                      // the value is an id list on this key. Cast on the RECEIVER only, so the optional
                      // chain still short-circuits exactly as it does today. Erased at emit.
                      (attachEntity[formCategories.progressUpdates] as ObjectIdString[])?.length > 0
                    }
                  />
                )}

                {inspectionEntityCondition && (
                  <RadioButton
                    label={isRustomjeePortal ? t("Work Done") : t("Inspections")}
                    onChange={() => setAttachEntity({ name: formCategories.inspection })}
                    checked={
                      attachEntity.name === formCategories.inspection ||
                      (attachEntity[formCategories.inspection] as ObjectIdString[])?.length > 0
                    }
                  />
                )}
              </span>
              <Box onClick={handleClearClick} className="bg-button_light rounded-2xl h-6 ps-2 pe-2 cursor-pointer">
                <Text>{t("Clear")}</Text>
              </Box>
            </span>
          </div>
        )}
        {props.showFormFlowCreationModal.category !== formCategories.changeOrder && (
          <div>
            <CheckBoxComponent
              // `checked` is optional on the checkbox's event shape but the state is a plain boolean.
              // Asserted, not coerced: `!!` would emit, `!` does not.
              onChange={(args: { checked?: boolean }) => setRememberUserSettings(args.checked!)}
              checked={rememberUserSettings}
              label="Remember the configuration being applied in this series of steps for subsequent form(s) creation"
            />
          </div>
        )}
      </div>
    );
  };

  const handleTemplateSelect = (params?: TemplateTreeNode | null) => {
    if (!params) {
      setSelectedForm({});
      return;
    }
    if (!params.parentId) {
      setSelectedForm({});
      dispatch(
        showToastNotification({
          content: t("Please select a template and not template type to continue"),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }
    if (params) {
      handleSelectTemplate(params);
    }
  };

  const handleClearClick = () => {
    setAttachEntity({ name: "" });
  };

  const formsFooterContent = () => {
    let isCreateDisable = !title || !selectedForm._id || !selectedFormFlow._id || !spaceId;
    return (
      <div className=" flex gap-4">
        <ButtonComponent
          disabled={isCreateDisable}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleCreateClick as typeof handleCreateClick & React.MouseEventHandler<HTMLButtonElement>}
          loading={showLoader}
        >
          <Text>{t("Create")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleCancelFormAttachment}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };

  const getInspectionFormFlow = () => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      footerContent={formsFooterContent()}
      width="60%"
      headerTitle={formsHeaderContent()}
      onCancel={handleCancelFormAttachment}
      isVisible={props.showFormFlowCreationModal.status}
      modelContent={formsModelContent()}
    />
  );

  const closeAttachEntityModal = () => {
    setAttachEntity({ name: "budget" });
  };

  const handleAttachEntityClick = () => {
    if (attachEntity.name == "tasks") {
      const slick = treeGrid.current?.slickGrid;
      const selectedTasks = (slick?.getSelectedRows?.() ?? []).map((data) => slick?.getDataItem?.(data));
      if (!selectedTasks.length) {
        dispatch(
          showToastNotification({
            content: t("Select activities first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
        return;
      }
      // `getDataItem` is declared as possibly-absent on the grid handle, so the mapped rows are
      // `| undefined`; the ids are branded 24-hex strings on the wire. Both assertions erased at emit —
      // in particular `task!._id` keeps the unguarded read, so a missing row still throws as before.
      const taskIds = selectedTasks.map((task) => task!._id);
      setAttachEntity({ name: "", [attachEntity.name]: taskIds as ObjectIdString[] });
    } else if (attachEntity.name == formCategories.progressUpdates) {
      const selectedProgressUpdates = formsRef.current?.dataView?.getAllSelectedItems() || [];
      if (selectedProgressUpdates.length) {
        const progressUpdateIds = selectedProgressUpdates.map((form) => form._id);
        setAttachEntity({ name: "", [attachEntity.name]: progressUpdateIds as ObjectIdString[] });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select Progress Updates(s) first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    } else if (attachEntity.name == formCategories.inspection) {
      const selectedInspection = formsRef.current?.dataView?.getAllSelectedItems() || [];
      if (selectedInspection.length) {
        const inspectionIds = selectedInspection.map((form) => form._id);
        setAttachEntity({ name: "", [attachEntity.name]: inspectionIds as ObjectIdString[] });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select Inspections(s) first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    } else if (attachEntity.name == "milestones") {
      const selectedMileStones = milestoneRef.current?.dataView?.getAllSelectedItems?.() || [];
      if (selectedMileStones.length) {
        const milestoneIds = selectedMileStones.map((mileStone) => mileStone._id);
        setAttachEntity({ name: "", [attachEntity.name]: milestoneIds as ObjectIdString[] });
      } else {
        dispatch(
          showToastNotification({
            content: t("Select Milestone(s) first"),
            type: TOAST_TYPE_WARNING,
          }),
        );
      }
    }
  };

  const attachEntityFooter = () => {
    const isModuleDisabled = params.contractId && !contractDetails?.modulesEnabled[attachEntity.name];
    return (
      <div className="flex gap-4">
        <ButtonComponent
          disabled={isModuleDisabled as boolean | undefined}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          onClick={handleAttachEntityClick}
        >
          <Text>{t("Attach")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={closeAttachEntityModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  };
  const attachEntityHeader = () => (
    <div className="w-full flex justify-between rounded-md">
      <div className="flex flex-col gap-1">
        <div>{`Attach ${defaultModuleNamesMapper[attachEntity.name]}`}</div>
        <span className="text-sm font-normal">{`All the ${defaultModuleNamesMapper[attachEntity.name]} related to this Contract`}</span>
      </div>
    </div>
  );
  const attachEntityContent = () => {
    // KNOWN BUG, preserved verbatim: two prop-contract mismatches the `any` aliases were hiding.
    // (1) `TasksForms` declares NO `projectId` prop — the one passed below is dead weight (that
    //     component reads `useParams().projectId` itself).
    // (2) `MileStones` declares `contractDetails` as a REQUIRED prop and nothing is passed here, so
    //     that grid mounts with `contractDetails: undefined` and throws on its first read of it. In
    //     practice this modal only opens on a contract screen where the grid is reached via a
    //     different mount, which is why it is not seen.
    // Each alias widens the component's own prop type by exactly the mismatch and is erased at emit;
    // the fixes belong in those two components' prop declarations.
    const AnyTasksForms = TasksForms as React.ComponentType<
      React.ComponentProps<typeof TasksForms> & { projectId?: string }
    >;
    const AnyMilestone = MileStones as React.ComponentType<Partial<React.ComponentProps<typeof MileStones>>>;
    const AnyContractTasks: typeof ContractTasks = ContractTasks;
    if (attachEntity.name === "tasks") {
      return <AnyContractTasks ref={treeGrid} contractId={params.contractId} />;
    }
    if (attachEntity.name === formCategories.progressUpdates) {
      return (
        <div className=" h-full flex overflow-auto">
          <AnyTasksForms
            showOnlyApproved
            source="contract"
            ref={formsRef}
            showOnlyGrid
            formCategory={formCategories.progressUpdates}
            projectId={params.projectId}
          />
        </div>
      );
    }
    if (attachEntity.name === formCategories.inspection) {
      return (
        <div className="m-6 h-full flex overflow-auto">
          <AnyTasksForms
            showOnlyApproved
            source="contract"
            ref={formsRef}
            showOnlyGrid
            formCategory={formCategories.inspection}
            projectId={params.projectId}
          />
        </div>
      );
    }
    if (attachEntity.name === "milestones") {
      return (
        <div className="m-6 h-full flex overflow-auto">
          <AnyMilestone
            showOnlyGrid
            // `milestoneRef` is declared with this file's own `AttachGridHandle`; the milestones grid
            // exposes `MileStoneGridHandle`. The two describe the same imperative handle from either
            // side. Erased assertion at the one boundary.
            ref={milestoneRef as ComponentProps<typeof AnyMilestone>["ref"]}
            // Route param, so unbranded; the grid declares the branded 24-hex form. Erased at emit.
            contractId={params.contractId as ObjectIdString}
            projectId={params.projectId}
          />
        </div>
      );
    }
  };

  const previewContent = () => {
    if (showPreview == "Template") {
      return <FormPreview formData={selectedForm} />;
    } else if (showPreview == "FormFlow") {
      return (
        <FormFlowCreationNew
          showLoader={showLoader}
          formFlowId={selectedFormFlow._id}
          // `spaceId` state carries `null` (no space resolved yet) and `undefined` (prop omitted); the
          // editor declares `string | undefined`. Erased at emit, so a null still goes through.
          spaceId={spaceId!}
          projectId={props.projectId}
          previewMode
        />
      );
    }
    return <></>;
  };
  return (
    <div>
      {props.showFormFlowCreationModal.status && getInspectionFormFlow()}
      {allowedAttachmentConstants.includes(attachEntity.name) && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          footerContent={attachEntityFooter()}
          width="80%"
          height="80%"
          headerTitle={attachEntityHeader()}
          onCancel={closeAttachEntityModal}
          isVisible
          modelContent={attachEntityContent()}
        />
      )}
      {showPreview && (
        <ModalStateDialogComponent
          footerContent={
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => setShowPreview(null)}>
              {t("Close")}
            </ButtonComponent>
          }
          allowModalPadding={false}
          width="80%"
          height="80%"
          headerTitle={<>{`${showPreview} | ${t("Preview")}`}</>}
          onCancel={() => setShowPreview(null)}
          isVisible
          modelContent={previewContent()}
        />
      )}
      {showCreateFlowModal && (
        <ModalStateDialogComponent
          allowModalPadding={false}
          width="80%"
          height="90%"
          headerTitle={<>{t("New Flow")}</>}
          onCancel={() => setShowCreateFlowModal(false)}
          isVisible
          modelContent={
            <FormFlowCreationNew
              showLoader={showLoader}
              handleCancelFormAttachment={() => setShowCreateFlowModal(false)}
              handleCreateClick={handleCreateClick}
              spaceId={spaceId!}
              source="FORM_CREATION"
              projectId={props.projectId}
              selectedTemplate={selectedForm}
            />
          }
        />
      )}
    </div>
  );
}
export default InspectionCreation;
